import { useClerk } from "@clerk/tanstack-react-start";
import * as Sentry from "@sentry/tanstackstart-react";
import type { AnyRoute } from "@tanstack/react-router";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { AlertCircle, Info } from "lucide-react";
import { useCallback } from "react";
import { z } from "zod";

import { AuthCard } from "~/components/auth/auth-card";
import { AuthFlow } from "~/components/auth/auth-flow";
import { AuthShell } from "~/components/auth/auth-shell";
import { isAuthError } from "~/lib/auth/auth-error";
import { saveUserSignup } from "~/lib/auth/save-user-signup";
import { getCoachStatus } from "~/lib/functions/coach-status";
import {
  clearPostAuthCookiesClientSide,
  consumePostAuthIntentFn,
} from "~/lib/functions/user-intent";
import { createNoIndexMeta } from "~/lib/shared/meta";
import {
  resolvePostAuthDestination,
  splitDestinationPath,
  validateRedirectPath,
} from "~/lib/shared/post-auth-routing";

const loginSearchSchema = z.object({
  from: z
    .enum([
      "auth-failure",
      "accept-token-failure",
      "magic-link-device-mismatch",
      "magic-link-expired",
      "magic-link-already-used",
      "magic-link-failed",
    ])
    .optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/_auth/login/")({
  validateSearch: (search: Record<string, unknown>) =>
    loginSearchSchema.parse(search),
  head: () => {
    const metaConfig = createNoIndexMeta(
      "Log in – Dream Coach Match",
      "Sign in or create your Dream Coach Match account.",
    );

    return {
      ...metaConfig,
      links: [{ rel: "canonical", href: "https://dreamcoachmatch.com/login" }],
    };
  },
  staticData: { title: "Log in" },
  component: LoginPage,
});

function InfoBanner({
  variant,
  title,
  message,
}: {
  variant: "info" | "error";
  title: string;
  message: string;
}) {
  const styles =
    variant === "info"
      ? "border-blue-200 bg-blue-50 text-blue-700"
      : "border-red-200 bg-red-50 text-red-700";

  const Icon = variant === "info" ? Info : AlertCircle;

  return (
    <div className={`flex gap-3 rounded-lg border p-4 text-sm ${styles}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-medium">{title}</p>
        <p className="mt-1">{message}</p>
      </div>
    </div>
  );
}

const BANNER_CONFIG: Record<
  string,
  { variant: "info" | "error"; title: string; message: string }
> = {
  "accept-token-failure": {
    variant: "error",
    title: "We couldn't automatically sign you in",
    message: "Please sign in with your email below to continue.",
  },
  "magic-link-device-mismatch": {
    variant: "error",
    title: "Sign-in link opened on a different device",
    message:
      "The sign-in link you clicked was requested from a different browser or device. For security, please request a new link from this device.",
  },
  "magic-link-expired": {
    variant: "info",
    title: "Your sign-in link has expired",
    message:
      "Sign-in links are valid for a limited time. Enter your email below to get a fresh one.",
  },
  "magic-link-already-used": {
    variant: "info",
    title: "This link was already used",
    message:
      "You may already be signed in on the device where you originally requested the link. To sign in here, enter your email below.",
  },
  "magic-link-failed": {
    variant: "info",
    title: "Something went wrong with your sign-in link",
    message:
      "No worries — your account is safe. Enter your email below to try again.",
  },
};

function LoginPage() {
  const navigate = useNavigate();
  const clerk = useClerk();
  const search = useSearch({ from: "/_auth/login/" });
  // Hardened against open redirects (R: today's bare-string cast was a gap) —
  // an invalid `?redirect=` falls through to the intent-based default below.
  const validatedSearchRedirect = search?.redirect
    ? validateRedirectPath(search.redirect)
    : null;

  const handleSuccess = useCallback(async () => {
    // Handle anonymous user linking for new signups
    const userId = clerk?.user?.id;
    if (userId && clerk?.user?.createdAt) {
      const createdAt = new Date(clerk.user.createdAt).getTime();
      const isRecentSignup = Date.now() - createdAt < 60_000;

      if (isRecentSignup) {
        try {
          await saveUserSignup({
            data: {
              firstName: clerk.user.firstName ?? "",
              lastName: clerk.user.lastName ?? "",
              email: clerk.user.primaryEmailAddress?.emailAddress ?? "",
            },
          });
        } catch (error) {
          // The account still works, but the anonymous discovery answers stay
          // unlinked. The server already reports its own failures. Both Sentry
          // filters drop a missing session as an auth error, yet a fresh
          // signup always has one, so report it here in a wrapper they keep.
          if (isAuthError(error)) {
            Sentry.captureException(
              new Error("Saving the new signup found no Clerk session", {
                cause: error,
              }),
              { tags: { surface: "signup_save" } },
            );
          }
        }
      }
    }

    // Consume any post-auth intent/destination cookies (discovery,
    // /become-a-coach entry points, OAuth full-page redirects that bypassed
    // this callback on signup) alongside coach-record status, then resolve
    // the landing surface via the shared precedence function (R10):
    // validated cookie destination > validated `?redirect=` > intent (coach
    // w/ record → coach home, coach w/o record → registration resume) >
    // client home. Resolving `isCoach` here — rather than leaving it to the
    // `_user` layout gate — lands coach-intent-no-record users directly on
    // `/become-a-coach` without an extra redirect hop through the coach home
    // (`/my-coaching`). Never blocks navigation on failure — falls
    // through to the validated search redirect or the client home.
    let destination: string;
    try {
      const [intentResult, coachStatus] = await Promise.all([
        consumePostAuthIntentFn(),
        getCoachStatus(),
      ]);
      destination = resolvePostAuthDestination({
        cookieDestination: intentResult.destination,
        searchRedirect: validatedSearchRedirect,
        intent: intentResult.intent,
        isCoach: coachStatus.isCoach,
      });
    } catch {
      // Non-critical — worst case the user lands on the unchanged default.
      destination = validatedSearchRedirect ?? "/dashboard";
    } finally {
      clearPostAuthCookiesClientSide();
    }

    // `to` is a pure path template to TanStack Router — an embedded `?query`
    // (e.g. the become-a-coach program-invite destination) is never parsed
    // out of it, so split it into `{ to, search }` before navigating.
    const { pathname, search } = splitDestinationPath(destination);
    navigate({
      to: pathname as AnyRoute["fullPath"],
      // `search`'s shape can't be statically resolved for a dynamically
      // computed destination — same reason `to` above is cast via
      // `AnyRoute["fullPath"]` rather than a literal route path.
      search: search as never,
    });
  }, [navigate, validatedSearchRedirect, clerk?.user]);

  const bannerFrom = search?.from;
  const authFailureBanner =
    bannerFrom === "auth-failure"
      ? {
          variant: "info" as const,
          title: clerk?.session ? "Session expired" : "Sign in required",
          message: clerk?.session
            ? "Your session has expired. Please sign in again to continue."
            : "Please sign in to access this page.",
        }
      : null;
  const banner =
    authFailureBanner ?? (bannerFrom ? BANNER_CONFIG[bannerFrom] : undefined);

  return (
    <AuthShell>
      <AuthCard>
        <div className="space-y-6">
          {banner && (
            <InfoBanner
              variant={banner.variant}
              title={banner.title}
              message={banner.message}
            />
          )}

          <AuthFlow
            onSuccess={handleSuccess}
            redirectUrl={
              typeof window !== "undefined"
                ? window.location.href
                : "/dashboard"
            }
          />
        </div>
      </AuthCard>
    </AuthShell>
  );
}
